import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import ICONS, { ICON_COLORS } from '../config/icon';
import { MonoButton, PaperDarkBG } from '../kits';
import { FaYoutube, FaVimeo, FaCloudSunRain } from 'react-icons/fa';
import RoughBorder from '../kits/LayoutKit/RoughBorder';

function CourseCard({ course }) {
  const cardRef = useRef();

  // Kurs-URL
  const coursePath = course?.slug ? `/course/${course.slug}` : `/course/${course?.id ?? ''}`;
  const Icon       = ICONS[course?.icon]        ?? ICONS.default;
  const colorClass = ICON_COLORS[course?.icon]  ?? ICON_COLORS.default;

  // Plattform-Badge
  const platformIcons = {
    YouTube: FaYoutube,
    Vimeo:   FaVimeo,
    Cloud:   FaCloudSunRain,
  };
  const PlatformIcon  = platformIcons[course?.platform] ?? null;
  const platformLabel = course?.platform ?? '';

  // Beschreibung normalisieren
  const descriptionItems = useMemo(() => {
    if (!course?.description) return [];

    if (Array.isArray(course.description)) return course.description;

    if (typeof course.description === 'string') {
      let str = course.description.trim();

      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        const unescaped = str.replace(/\\"/g, '"');
        try {
          const parsed = JSON.parse(unescaped);
          if (Array.isArray(parsed)) return parsed;
        } catch (e2) {}
      }

      if (str.startsWith('[') && str.endsWith(']')) {
        str = str.slice(1, -1);
      }
      return str
        .split(/\s*,\s*/)
        .map((s) => s.replace(/^"|"$/g, '').replace(/^'|'$/g, '').trim())
        .filter(Boolean);
    }

    return [String(course.description)];
  }, [course?.description]);

  return (
    <div ref={cardRef} className="relative">
      
      {/* Plattform‑Badge */}
      {PlatformIcon && (
        <div className="absolute -top-3 -left-3 flex flex-col items-center z-10">
          <div className=" border-black p-2 rounded-full hover:shadow-md -rotate-12">
            <PlatformIcon className="text-black w-6 h-6" />
          </div>
          <span className="text-[10px] text-black mt-1 bg-gray-50 px-1 py-0.5 rounded shadow-sm border border-gray-300">
            {platformLabel}
          </span>
        </div>
      )}

      {/* 📄 Karten‑Inhalt */}
    <div className="
  bg-gradient-to-br from-gray-50 via-orange-50/25 to-gray-50 
  rounded-xl p-6 flex flex-col h-full min-h-[350px] font-mono shadow-md
">
        {course ? (
          <>
            {/* Header */}
            <div className="flex items-start space-x-4 mb-4 bg-gray-950 rounded-md sm:rounded-lg pt-3 pl-3 ">
              <Icon className={`w-6 h-6 shrink-0 ${colorClass}`} />
              <h3 className="text-md text-white text-left tracking-tight mb-2 overflow-hidden text-ellipsis leading-tight min-h-[1.6rem]">
                {course.title}
              </h3>
            </div>

            {/* Fakten */}
            <div className="text-black border border-black rounded-md py-2 px-4 mb-4 font-mono leading-tight text-xs tabular-nums">
              <PaperDarkBG>
                <div className="flex justify-between items-center gap-1">
                  <span className="inline-block bg-gray-900 rounded-sm px-2 py-1 -ml-2 text-xs text-white">
                    Projekt Training
                  </span>
                  <span className="inline-block bg-gray-900 rounded-sm px-2 py-1 -mr-2 text-xs text-white">
                    mit {course.instructor}
                  </span>
                </div>
              </PaperDarkBG>
              <div className="flex justify-between"><span>Start:</span><span>sofort möglich</span></div>
              <div className="flex justify-between"><span>Dauer:</span><span>{course.durationDays && course.durationDays !== '0' ? `${course.durationDays} Tage` : 'Individuell'}</span></div>
              <div className="flex justify-between"><span>Online:</span><span>{course.priceOnline && course.priceOnline !== '0' ? `${course.priceOnline} €` : 'Preis auf Anfrage'}</span></div>
              <div className="flex justify-between"><span>Vor Ort:</span><span>{course.price && course.price !== '0' ? `${course.price} €` : 'Preis auf Anfrage'}</span></div>
            </div>

            {/* Beschreibung */}
            <ul className="pl-4 text-xs text-left text-gray-700 space-y-1 mb-4 list-[square]">
              {descriptionItems.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            {/* CTA */}
            <div className="mt-auto">
              <Link to={coursePath}>
                <MonoButton className='w-full mt-4 text-sm'>Diesen Kurs ansehen</MonoButton>
              </Link>
            </div>
          </>
        ) : (
          <>
            <p className="text-red-500">Kursinformationen sind derzeit leider nicht verfügbar.</p>
          </>
        )}
      </div>

      {/* RoughBorder GANZ AUSSEN */}
      <RoughBorder
        parentRef={cardRef}
        radius={10}
        padding={3}
       color="#AC8E66" 
        strokeWidth={0.3}
        roughness={1}
        bowing={2}
      />
    </div>
  );
}

export default CourseCard;
